import { createContext, useState } from "react";
import type { StudioPreviewProduct } from "@repo/types";

interface PreviewContext {
  isPreview: boolean;
  setIsPreview: (isPreview: boolean) => void;
  /**
   * VERA-644 (TD-066 last-mile): the Studio preview's live white-label toggle
   * state, pushed from the platform over the `theme` postMessage. Only
   * meaningful when `isPreview` is true; consumed at the certificate mock
   * render site so the iframed preview reflects the WhiteLabelCard's local
   * (possibly unsaved) toggle without waiting for a save.
   */
  hidePoweredBy: boolean;
  setHidePoweredBy: (hidePoweredBy: boolean) => void;
  /** Studio is previewing the PUBLIC composition, not the owner's. */
  previewPublicView: boolean;
  setPreviewPublicView: (previewPublicView: boolean) => void;
  /** Studio is previewing an EMBEDDED install (the brand's own site), not the hosted one. */
  previewEmbedded: boolean;
  setPreviewEmbedded: (previewEmbedded: boolean) => void;
  /**
   * A REAL certificate to render in preview, from `?certificate_id=` on the preview URL.
   *
   * The preview route has no `:certificateId` path param, so `CertificatePage`'s
   * `useParams()` yields undefined there and the only thing it could ever show was
   * `certificatesMock[0]`. That is right for Studio, which previews a THEME and wants
   * stable filler — but it also meant a brand had no way to look at one of its own
   * certificates without activating it, and activation is a billable event. Carrying the
   * id on the context lets the same preview serve both: absent -> mock (Studio's
   * behaviour, unchanged), present -> the real record.
   */
  previewCertificateId: string | null;
  setPreviewCertificateId: (certificateId: string | null) => void;
  /**
   * The brand's OWN product to render inside the mock certificate (VRG-827),
   * pushed from Studio over the same `postMessage` bridge as the theme.
   *
   * Distinct from `previewCertificateId` above, and deliberately so: that one
   * swaps the WHOLE record for a real one and needs a certificate to exist.
   * Studio has no certificate to point at — a brand setting up its theme has
   * products long before it has certificates, and activation is billable. This
   * keeps the mock's scaffolding and replaces only the product, so the preview
   * works from the brand's very first product.
   */
  previewProduct: StudioPreviewProduct | null;
  setPreviewProduct: (product: StudioPreviewProduct | null) => void;
}

export const PreviewContext = createContext<PreviewContext>({} as PreviewContext);

export const PreviewContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isPreview, setIsPreview] = useState(false);
  const [hidePoweredBy, setHidePoweredBy] = useState(false);
  const [previewPublicView, setPreviewPublicView] = useState(false);
  const [previewEmbedded, setPreviewEmbedded] = useState(false);
  const [previewCertificateId, setPreviewCertificateId] = useState<string | null>(null);
  const [previewProduct, setPreviewProduct] = useState<StudioPreviewProduct | null>(null);

  return (
    <PreviewContext.Provider
      value={{
        isPreview,
        setIsPreview,
        hidePoweredBy,
        setHidePoweredBy,
        previewPublicView,
        setPreviewPublicView,
        previewEmbedded,
        setPreviewEmbedded,
        previewCertificateId,
        setPreviewCertificateId,
        previewProduct,
        setPreviewProduct,
      }}
    >
      {children}
    </PreviewContext.Provider>
  );
};